import { String_extractIndex } from "../../common/stringUtils";
import Person from "../Person";
import Errors from "../responses/Errors";

type PersonFormElement = {
    name: string;
    nameErrorMessage?: string;
    phoneNumber: string;
    phoneNumberErrorMessage?: string;
    isActive: boolean;
};

export default PersonFormElement;

export function PersonFormElement_default(): PersonFormElement {
    return {
        isActive: false,
        name: "",
        phoneNumber: ""
    };
}

export function PersonFormElement_toPerson(personFormElement: PersonFormElement): Person {
    const person: Person = {
        isActive: personFormElement.isActive,
        name: personFormElement.name,
        phoneNumber: personFormElement.phoneNumber
    };

    return person;
}

export function PersonFormElement_populateErrorMessages<TPersonFormElement extends PersonFormElement>(
    formElement: TPersonFormElement,
    errors: Errors
): TPersonFormElement {
    const errorKeys = Object.keys(errors);

    const updatedFormElement: TPersonFormElement = {
        ...formElement,
        nameErrorMessage: undefined,
        phoneNumberErrorMessage: undefined
    };

    errorKeys.forEach((k) => {
        const isNameErrorMessage = k.endsWith("Name");
        const isPhoneNumberErrorMessage = k.endsWith("PhoneNumber");

        if (isNameErrorMessage) {
            updatedFormElement.nameErrorMessage = errors[k][0];
        } else if (isPhoneNumberErrorMessage) {
            updatedFormElement.phoneNumberErrorMessage = errors[k][0];
        }
    });

    return updatedFormElement;
}

/**
 * Returns a new array of PersonFormElements with error messages populated from the given errors object.
 * @param formElements The array of PersonFormElements to update. Will not be mutated.
 * @param errors The errors object to use for populating the error messages. Each error key needs to have format "[index].Property". For example, "[0].Name" or "[32].PhoneNumber".
 */
export function PersonFormElements_populateErrorMessages<TPersonFormElement extends PersonFormElement>(
    formElements: TPersonFormElement[],
    errors: Errors
): TPersonFormElement[] {
    const errorsKeys = Object.keys(errors);

    //
    // Groups the errors by the index extracted from the error key, and removes the index from the key.
    //
    const groupedErrors: { [personIndex: number]: Errors } = {};
    errorsKeys.forEach((k) => {
        const index = String_extractIndex(k);

        if (!Number.isInteger(index) || (index as number) >= formElements.length) {
            console.error("Invalid index extracted from error key", index);
            return;
        }

        const numberIndex = index as number;

        if (!groupedErrors[numberIndex]) {
            groupedErrors[numberIndex] = {} as Errors;
        }

        const reformatedErrorKey = k.substring(k.indexOf(".") + 1);

        //
        // The reformated key will have the same errors as the corresponding key in the original errors object.
        //
        groupedErrors[numberIndex][reformatedErrorKey] = errors[k];
    });

    const updatedPersonFormElements: TPersonFormElement[] = formElements.map((p, index) => {
        const personErrors = groupedErrors[index] ?? {};

        return PersonFormElement_populateErrorMessages(p, personErrors);
    });

    return updatedPersonFormElements;
}
